import React, { useState, memo, useCallback } from 'react';
import { View, Image, Animated, ActivityIndicator } from 'react-native';
import { Recipe, getFirstImageUrl } from '@coffeeplexity/shared';
import { Text } from './Text';
import { Badge } from './Badge';
import { Avatar } from './Avatar';
import { Card } from './Card';
import { FloatingActionButton } from './FloatingActionButton';
import { useTheme } from '../themes';
import { colorTokens } from '../tokens';
import { Heart, Eye, Bookmark, Star, Clock } from 'lucide-react-native';

export interface RecipeCardProps {
  recipe: Recipe;
  onPress: (recipe: Recipe) => void;
  onLike?: (recipeId: string) => void;
  onBookmark?: (recipeId: string) => void;
  liked?: boolean;
  bookmarked?: boolean;
  variant?: 'default' | 'compact' | 'featured';
  testID?: string;
  headerAccessory?: React.ReactNode;
}

export const RecipeCard = memo(function RecipeCard({
  recipe,
  onPress,
  onLike,
  onBookmark,
  liked = false,
  bookmarked = false,
  variant = 'default',
  testID,
  headerAccessory,
}: RecipeCardProps) {
  const { theme, isDark } = useTheme();
  const [imageLoaded, setImageLoaded] = useState(false);
  const fadeAnim = React.useRef(new Animated.Value(1)).current;
  const DEFAULT_IMAGE_URI =
    'https://placehold.co/1200x800/EEE/AAA?text=Coffee+Recipe';

  const getImageHeight = () => {
    switch (variant) {
      case 'compact':
        return 120;
      case 'featured':
        return 250;
      default:
        return 200;
    }
  };

  const getFallbackImage = useCallback((): string => {
    const hashString = (input: string): number => {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        hash = (hash << 5) - hash + input.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash);
    };

    const pools: Record<string, string[]> = {
      default: [
        'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1517705008128-361805f42e86?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1511920170033-f8396924c348?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=1200&auto=format&fit=crop',
      ],
      pour_over: [
        'https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1503481766315-7a586b20f66d?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=1200&auto=format&fit=crop',
      ],
      chemex: [
        'https://images.unsplash.com/photo-1503481766315-7a586b20f66d?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1541167760496-1628856ab772?w=1200&auto=format&fit=crop',
      ],
      french_press: [
        'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1526403641449-2668376983e8?w=1200&auto=format&fit=crop',
      ],
      aeropress: [
        'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=1200&auto=format&fit=crop',
      ],
      espresso: [
        'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=1200&auto=format&fit=crop',
      ],
      moka_pot: [
        'https://images.unsplash.com/photo-1511920170033-f8396924c348?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1542736667-069246bdbc6d?w=1200&auto=format&fit=crop',
      ],
      cold_brew: [
        'https://images.unsplash.com/photo-1517705008128-361805f42e86?w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1461988320302-91bde64fc8e4?w=1200&auto=format&fit=crop',
      ],
    };

    const method = (recipe.brewing_method || '')
      .toLowerCase()
      .replace(/\s+/g, '_');
    const pool =
      pools[method] && pools[method]!.length > 0
        ? pools[method]!
        : pools.default;
    const seed = recipe.id || `${method}-${recipe.title || 'recipe'}`;
    const index = hashString(seed) % pool.length;
    return pool[index];
  }, [recipe.id, recipe.brewing_method, recipe.title]);

  const imageUri = getFirstImageUrl(recipe.images) || getFallbackImage();
  const [resolvedUri, setResolvedUri] = useState<string>(imageUri);

  React.useEffect(() => {
    setResolvedUri(imageUri);
  }, [imageUri]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: theme.motion.duration.normal,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim, theme.motion.duration.normal]);

  const handleImageError = useCallback(() => {
    const fallback = getFallbackImage();
    if (resolvedUri !== fallback) {
      setResolvedUri(fallback);
      setImageLoaded(false);
      return;
    }
    if (resolvedUri !== DEFAULT_IMAGE_URI) {
      setResolvedUri(DEFAULT_IMAGE_URI);
      setImageLoaded(false);
      return;
    }
    setImageLoaded(true);
  }, [resolvedUri, getFallbackImage, DEFAULT_IMAGE_URI]);

  const handleCardPress = useCallback(() => {
    onPress(recipe);
  }, [onPress, recipe]);

  const handleLikePress = useCallback(() => {
    onLike?.(recipe.id);
  }, [onLike, recipe.id]);

  const handleBookmarkPress = useCallback(() => {
    onBookmark?.(recipe.id);
  }, [onBookmark, recipe.id]);

  return (
    <Card
      variant='default'
      elevation={0}
      onPress={handleCardPress}
      style={{
        width: '100%',
        marginBottom: theme.spacing.spacing[3],
        // Minimal, crisp surface using DS tokens
        backgroundColor: isDark
          ? theme.colors.surface.secondary
          : theme.colors.surface.primary,
        borderWidth: 1,
        borderColor: theme.colors.border.subtle,
        shadowColor: '#000',
        shadowOpacity: isDark ? 0.12 : 0.04,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 3 },
        elevation: 1,
      }}
      testID={testID}
      dotPattern={variant === 'featured'}
      borderRadius='md'
      padding={0}
    >
      {/* Image Container with Elegant Loading */}
      {
        <View style={{ position: 'relative', width: '100%' }}>
          <View
            style={{
              width: '100%',
              height: getImageHeight(),
              backgroundColor: colorTokens.neutral[100],
              borderTopLeftRadius: theme.spacing.borderRadius.xl,
              borderTopRightRadius: theme.spacing.borderRadius.xl,
              overflow: 'hidden',
            }}
          >
            {/* Loading Placeholder */}
            {!imageLoaded && (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: colorTokens.neutral[50],
                }}
              >
                <ActivityIndicator
                  size='small'
                  color={colorTokens.primary[500]}
                />
              </View>
            )}

            {/* Main Image */}
            <Animated.View
              style={{
                opacity: fadeAnim,
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <Image
                source={{ uri: resolvedUri }}
                style={{
                  width: '100%',
                  height: '100%',
                }}
                resizeMode='cover'
                onLoad={handleImageLoad}
                onLoadEnd={() => setImageLoaded(true)}
                onError={handleImageError}
                // Performance optimizations
                fadeDuration={0}
              />
            </Animated.View>
            {headerAccessory && (
              <View
                pointerEvents='none'
                style={{
                  position: 'absolute',
                  top: theme.spacing.spacing[2],
                  left: theme.spacing.spacing[2],
                  zIndex: 3,
                }}
              >
                {headerAccessory}
              </View>
            )}

            {/* Subtle overlay for readability */}
            <View
              pointerEvents='none'
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: getImageHeight() * 0.45,
                backgroundColor: isDark
                  ? 'rgba(0,0,0,0.25)'
                  : 'rgba(0,0,0,0.15)',
              }}
            />

            {/* Metadata badges over media */}
            <View
              style={{
                position: 'absolute',
                bottom: theme.spacing.spacing[2],
                left: theme.spacing.spacing[2],
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing.spacing[2],
              }}
              pointerEvents='none'
            >
              <Badge variant='brewing-method' size='sm'>
                {recipe.brewing_method.replace(/_/g, ' ')}
              </Badge>
              <Badge variant='difficulty' size='sm'>
                {recipe.difficulty}
              </Badge>
            </View>

            {/* Floating Action Buttons */}
            {(onLike || onBookmark) && (
              <View
                style={{
                  position: 'absolute',
                  top: theme.spacing.spacing[3],
                  right: theme.spacing.spacing[3],
                  flexDirection: 'row',
                  gap: theme.spacing.spacing[2],
                }}
              >
                {onLike && (
                  <FloatingActionButton
                    variant='glass'
                    size='sm'
                    onPress={handleLikePress}
                    testID='like-button'
                  >
                    <Heart
                      size={16}
                      color={liked ? '#ff4757' : theme.colors.text.primary}
                      fill={liked ? '#ff4757' : 'none'}
                    />
                  </FloatingActionButton>
                )}
                {onBookmark && (
                  <FloatingActionButton
                    variant='glass'
                    size='sm'
                    onPress={handleBookmarkPress}
                    testID='bookmark-button'
                  >
                    <Bookmark
                      size={16}
                      color={
                        bookmarked
                          ? (colorTokens.primary?.[500] as unknown as string) ||
                            (theme.colors.text.primary as unknown as string)
                          : (theme.colors.text.primary as unknown as string)
                      }
                      fill={
                        (bookmarked
                          ? (colorTokens.primary?.[500] as unknown as string)
                          : 'none') as any
                      }
                    />
                  </FloatingActionButton>
                )}
              </View>
            )}
          </View>
        </View>
      }

      {/* Content Section */}
      <View
        style={{
          padding: theme.spacing.spacing[4],
          // Ensure text legibility in light mode
          backgroundColor: 'transparent',
        }}
      >
        {/* Title with Apple-inspired Typography */}
        <Text
          variant='h5'
          style={{
            marginBottom: theme.spacing.spacing[2],
            fontWeight: '600',
            color: theme.colors.text.primary,
          }}
          numberOfLines={2}
        >
          {recipe.title}
        </Text>

        {/* Creator Information */}
        {recipe.creator && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: theme.spacing.spacing[3],
            }}
          >
            <Avatar
              size='xs'
              source={
                recipe.creator.avatar_url
                  ? { uri: recipe.creator.avatar_url }
                  : undefined
              }
              name={
                recipe.creator.first_name && recipe.creator.last_name
                  ? `${recipe.creator.first_name} ${recipe.creator.last_name}`
                  : recipe.creator.display_name
              }
            />
            <Text
              variant='bodySmall'
              color='secondary'
              style={{
                marginLeft: theme.spacing.spacing[2],
                color: theme.colors.text.secondary,
              }}
            >
              by{' '}
              {recipe.creator.first_name && recipe.creator.last_name
                ? `${recipe.creator.first_name} ${recipe.creator.last_name}`
                : recipe.creator.display_name}
            </Text>
          </View>
        )}

        {/* Spacer after media overlay badges */}
        <View style={{ height: theme.spacing.spacing[1] }} />

        {/* Stats with Elegant Presentation */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: theme.spacing.spacing[3],
          }}
        >
          <View style={{ flexDirection: 'row', gap: theme.spacing.spacing[4] }}>
            {/* Brew Time */}
            {recipe.brewing_parameters?.total_brew_time_seconds && (
              <StatItem
                icon={<Clock size={16} color={theme.colors.text.secondary} />}
                value={formatBrewTime(
                  recipe.brewing_parameters.total_brew_time_seconds
                )}
              />
            )}
            <StatItem
              icon={<Heart size={16} color={theme.colors.text.secondary} />}
              value={recipe.favorite_count}
            />
            <StatItem
              icon={<Eye size={16} color={theme.colors.text.secondary} />}
              value={recipe.view_count}
            />
            {recipe.rating && (
              <StatItem
                icon={<Star size={16} color={theme.colors.text.secondary} />}
                value={recipe.rating.toFixed(1)}
              />
            )}
          </View>
        </View>

        {/* Tags */}
        {recipe.tags && recipe.tags.length > 0 && (
          <View style={{ marginTop: theme.spacing.spacing[2] }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {recipe.tags.slice(0, 3).map((tag, index) => (
                <View
                  key={index}
                  style={{
                    backgroundColor: '#f3f4f6',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 16,
                  }}
                >
                  <Text
                    variant='caption'
                    style={{
                      color: '#374151',
                      fontWeight: '500',
                      fontSize: 10,
                    }}
                  >
                    {tag.replace(/_/g, ' ')}
                  </Text>
                </View>
              ))}
              {recipe.tags.length > 3 && (
                <View
                  style={{
                    backgroundColor: '#f3f4f6',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 16,
                  }}
                >
                  <Text
                    variant='caption'
                    style={{
                      color: '#374151',
                      fontWeight: '500',
                      fontSize: 10,
                    }}
                  >
                    +{recipe.tags.length - 3}
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}
      </View>
    </Card>
  );
});

// Nothing-inspired dot pattern overlay component
function DotPatternOverlay() {
  const { theme } = useTheme();

  return (
    <View style={{ flex: 1 }}>
      {Array.from({ length: 50 }).map((_, index) => (
        <View
          key={index}
          style={{
            position: 'absolute',
            width: 2,
            height: 2,
            backgroundColor: theme.colors.text.primary,
            borderRadius: 1,
            left: (index % 10) * 30 + 15,
            top: Math.floor(index / 10) * 30 + 15,
          }}
        />
      ))}
    </View>
  );
}

interface StatItemProps {
  icon: React.ReactNode;
  value: number | string;
}

function StatItem({ icon, value }: StatItemProps) {
  const { theme } = useTheme();

  const formatValue = (val: number | string) => {
    if (typeof val === 'number' && val > 1000) {
      return `${(val / 1000).toFixed(1)}k`;
    }
    return val;
  };

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.spacing[1],
      }}
    >
      {icon}
      <Text
        variant='caption'
        style={{
          color: theme.colors.text.tertiary,
          fontFamily: 'monospace', // Nothing-inspired monospace for stats
        }}
      >
        {formatValue(value)}
      </Text>
    </View>
  );
}

// Helper function to format brew time
function formatBrewTime(seconds: any): string {
  // Comprehensive validation
  if (seconds === null || seconds === undefined) {
    return 'N/A';
  }

  // Convert to number if it's a string
  let numericSeconds: number;
  if (typeof seconds === 'string') {
    numericSeconds = parseFloat(seconds);
    if (isNaN(numericSeconds)) {
      return 'N/A';
    }
  } else if (typeof seconds === 'number') {
    numericSeconds = seconds;
  } else {
    return 'N/A';
  }

  // Validate the numeric value
  if (isNaN(numericSeconds) || numericSeconds < 0) {
    return 'N/A';
  }

  // Handle edge cases
  if (numericSeconds === 0) return '0:00';
  if (numericSeconds < 60)
    return `0:${Math.floor(numericSeconds).toString().padStart(2, '0')}`;

  const minutes = Math.floor(numericSeconds / 60);
  const remainingSeconds = Math.floor(numericSeconds % 60);

  // Ensure we don't exceed reasonable limits
  if (minutes > 999) return 'N/A';

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}
